/**
 * Authentication Context
 *
 * Provides authentication state and methods using AWS Amplify v6
 * Manages user sessions, sign in/up, and sign out functionality
 */

/* eslint-disable react-refresh/only-export-components */

import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  getCurrentUser,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { notifications } from '@mantine/notifications';
import { getAuthErrorMessage } from '../../../utils/authErrors';
import type { AuthContextType } from '../../../types/auth';
import type { AuthUser } from 'aws-amplify/auth';
import {
  initializeAnalytics,
  trackSignupCompleted,
  trackLoginCompleted,
  trackLogout,
} from '../../../utils/analytics';

/**
 * Authentication context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Wraps the application and provides authentication state and methods
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if user is authenticated on mount
   * Also handles OAuth callback (Amplify automatically exchanges code for tokens)
   */
  useEffect(() => {
    void checkAuthStatus();

    // Set up interval to check auth status periodically during OAuth flow
    // This handles the case where Amplify is still exchanging tokens
    const interval = setInterval(() => {
      // Only check if we're not already authenticated
      if (!user) {
        void checkAuthStatus();
      }
    }, 2000);

    // Cleanup interval after 30 seconds (OAuth should complete by then)
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally run only once on mount to avoid infinite loop
  }, []);

  /**
   * Global Hub listener for auth events.
   * Handles token refresh failures and sign-outs that happen outside
   * the normal sign-in/sign-out flow (e.g. expired refresh token mid-session).
   *
   * @see https://docs.amplify.aws/react/build-a-backend/auth/connect-your-frontend/listen-to-auth-events/
   */
  useEffect(() => {
    const cancel = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'tokenRefresh_failure':
          setUser(null);
          notifications.show({
            title: 'Sesja wygasła',
            message: 'Twoja sesja wygasła. Zaloguj się ponownie.',
            color: 'orange',
            autoClose: 5000,
          });
          break;

        case 'signedOut':
          setUser(null);
          break;
      }
    });

    return () => cancel();
  }, []);

  /**
   * Check current authentication status
   */
  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser);

      // GA4: Initialize analytics for authenticated user
      initializeAnalytics(true, currentUser.userId);
    } catch (err) {
      // User is not authenticated
      setUser(null);

      // GA4: Initialize analytics for anonymous user
      initializeAnalytics(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const { isSignedIn, nextStep } = await amplifySignIn({
        username: email,
        password,
      });

      if (isSignedIn) {
        // Fetch user details after successful sign in
        const currentUser = await getCurrentUser();
        setUser(currentUser);

        // GA4: Track successful login
        trackLoginCompleted('email', currentUser.userId);
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        // User needs to confirm their account
        throw new Error('UserNotConfirmedException');
      }
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sign up a new user with email and password
   * @param email - User email address
   * @param password - User password
   * @param _termsAcceptedAt - ISO datetime string when user accepted T&C (validated in UI, not stored in Cognito)
   */
  const signUp = useCallback(async (email: string, password: string, _termsAcceptedAt: string) => {
    try {
      setLoading(true);
      setError(null);

      // NOTE: termsAcceptedAt is validated in the UI but not stored in Cognito
      // AWS Cognito does not allow adding custom attributes to existing User Pools
      // Terms acceptance is enforced by UI checkbox validation
      //
      // PHASE 2 ENHANCEMENT: Store terms acceptance timestamp in DynamoDB UserProfile
      // - Create UserProfile record on signup with termsAcceptedAt field
      // - Provides audit trail for GDPR compliance
      // - Allows tracking of T&C version acceptance
      // Phase 1 MVP: UI checkbox validation is sufficient for initial launch
      await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            // Removed: 'custom:termsAcceptedAt' - cannot add custom attributes to existing User Pool
          },
        },
      });

      // Note: User will need to confirm their email before they can sign in
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Confirm user signup with verification code
   */
  const confirmSignUp = useCallback(async (email: string, code: string) => {
    try {
      setLoading(true);
      setError(null);

      await amplifyConfirmSignUp({
        username: email,
        confirmationCode: code,
      });

      // GA4: Track successful signup completion
      trackSignupCompleted('email');
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // GA4: Track logout BEFORE signing out
      trackLogout();

      await amplifySignOut();
      setUser(null);
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request password reset (sends verification code to email)
   */
  const resetPassword = useCallback(async (email: string) => {
    try {
      setLoading(true);
      setError(null);

      await amplifyResetPassword({
        username: email,
      });
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Confirm password reset with code and new password
   */
  const confirmResetPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      try {
        setLoading(true);
        setError(null);

        await amplifyConfirmResetPassword({
          username: email,
          confirmationCode: code,
          newPassword,
        });
      } catch (err) {
        const errorMessage = getAuthErrorMessage(err);
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Sign in with Facebook OAuth
   */
  const signInWithFacebook = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Redirect to Facebook OAuth
      await signInWithRedirect({
        provider: 'Facebook',
      });

      // Note: User will be redirected to Facebook, then back to the app
      // The checkAuthStatus will be called on redirect callback
    } catch (err) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    confirmSignUp,
    resetPassword,
    confirmResetPassword,
    signInWithFacebook,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use authentication context
 *
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
